import { useMutation, useQuery } from '@tanstack/react-query'
import {
	ActionBar,
	Button,
	Dialog,
	Input,
	Text,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { GenericTable } from '../../../../components/GenericTable'
import { useToast } from '../../../../utils/ToastContext'
import { queryClient, trpc } from '../../../../utils/trpc'
import type { Cycle, CycleReviewerGroup, CycleReviewerGroupSearchInput } from '../../types'
import { CycleReviewersGroupModal } from './CycleReviewersGroupModal'

type CycleConfigurationFormValues = Pick<Cycle, 'cumulativeMaxReviewers' | 'cumulativeMinReviewers' | 'allowDuplicates'>

const CycleConfigurationForm = ({ cycle }: { cycle?: Cycle }) => {
	const toast = useToast()
	const { mutate: editCycle, isPending: isEditingCycle } = useMutation(
		trpc.admin.cycles.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Cycle configuration updated successfully', 'info')
			},
			onError: () => {
				toast.showToast('Failed to update cycle configuration', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.getById.queryKey({ id: cycle?.id }),
					exact: false,
					refetchType: 'all',
				})
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.search.queryKey(),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isDirty },
	} = useForm<CycleConfigurationFormValues>({
		defaultValues: {
			cumulativeMaxReviewers: cycle?.cumulativeMaxReviewers,
			cumulativeMinReviewers: cycle?.cumulativeMinReviewers,
			allowDuplicates: cycle?.allowDuplicates ?? false,
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	const onSubmit = (data: CycleConfigurationFormValues) => {
		if (!cycle) {
			return
		}

		if (Number(data.cumulativeMinReviewers) >= Number(data.cumulativeMaxReviewers)) {
			toast.showToast('Cumulative min raters must be less than max raters', 'error')
			return
		}

		editCycle({
			id: cycle.id,
			organizationId: cycle.organizationId,
			cumulativeMaxReviewers: Number(data.cumulativeMaxReviewers),
			cumulativeMinReviewers: Number(data.cumulativeMinReviewers),
			allowDuplicates: data.allowDuplicates,
			metadata: null,
			triggerLogId: null,
		})
	}

	return (
		<div>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
			<form className='mt-6 flex flex-row gap-4' onSubmit={handleSubmit(onSubmit)}>
				<Input
					label='Cumulative Min Raters'
					placeholder='Enter cumulative min raters'
					type='number'
					{...register('cumulativeMinReviewers', {
						required: 'Cumulative min raters is required',
					})}
					defaultValue={cycle?.cumulativeMinReviewers}
					hint={errors.cumulativeMinReviewers?.message}
					min={1}
					variant={errors.cumulativeMinReviewers ? 'error' : 'default'}
					disabled={isSubmitting}
				/>
				<Input
					label='Cumulative Max Raters'
					placeholder='Enter cumulative max raters'
					type='number'
					{...register('cumulativeMaxReviewers', {
						required: 'Cumulative max raters is required',
					})}
					defaultValue={cycle?.cumulativeMaxReviewers}
					hint={errors.cumulativeMaxReviewers?.message}
					min={1}
					variant={errors.cumulativeMaxReviewers ? 'error' : 'default'}
					disabled={isSubmitting}
				/>
				<Button
					type='submit'
					disabled={isSubmitting || !isDirty || isEditingCycle}
					isLoading={isEditingCycle}
					className='mt-4 w-auto self-end'>
					Save
				</Button>
			</form>
		</div>
	)
}

const CycleReviewerGroups = ({ cycle }: { cycle?: Cycle }) => {
	const [modalOpen, setModalOpen] = useState(false)
	const [selectedGroup, setSelectedGroup] = useState<CycleReviewerGroup>()
	const [selectedGroupsToDelete, setSelectedGroupsToDelete] = useState<CycleReviewerGroup[]>([])
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const toast = useToast()
	const deleteCycleReviewerGroupsMutation = useMutation(
		trpc.admin.cycles.reviewerGroups.delete.mutationOptions({
			onSuccess: () => {
				toast.showToast('Cycle rater groups deleted successfully', 'info')
			},
			onError: (error) => {
				toast.showToast(error.message || 'Failed to delete cycle rater groups', 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.reviewerGroups.search.queryKey({ cycleId: cycle?.id }),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	)

	if (!cycle) {
		return null
	}

	const handleDeleteSelected = () => {
		deleteCycleReviewerGroupsMutation.mutate({
			cycleId: cycle.id,
			reviewerGroupIds: selectedGroupsToDelete.map((group) => group.id),
		})
	}

	return (
		<div className='mt-6'>
			<Text variant={'h6'} weight='semibold' className='text-left mb-6'>
				Rater Groups
			</Text>

			<GenericTable<CycleReviewerGroup, CycleReviewerGroupSearchInput>
				selectable
				columns={[
					{
						label: 'Group Name',
						field: 'name',
						sortable: true,
					},
					{
						label: 'Show Group For',
						field: 'shownForRoles',
						sortable: false,
						render: (item) => {
							return item.shownForRoles.map((role) => role.displayName).join(', ')
						},
					},
					{
						label: 'Min Raters',
						field: 'minReviewers',
						sortable: true,
					},
					{
						label: 'Max Raters',
						field: 'maxReviewers',
						sortable: true,
					},
					{
						label: 'Required',
						field: 'required',
						sortable: true,
						render: (item) => {
							return item.required ? 'Yes' : 'No'
						},
					},
				]}
				onSelectionChange={setSelectedGroupsToDelete}
				actionButtons={[
					{
						children: 'Add Group',
						onClick: () => {
							setSelectedGroup(undefined)
							setModalOpen(true)
						},
					},
				]}
				queryOptionsGenerator={(options) =>
					trpc.admin.cycles.reviewerGroups.search.queryOptions({
						...options,
						cycleId: cycle.id,
						search: options.search || '',
					})
				}
				onRowClick={(item) => {
					setSelectedGroup(item)
					setModalOpen(true)
				}}
				emptyState={{
					title: 'No groups added',
					description: 'Get started by creating groups and adding raters',
					actionLabel: 'Create Rater Group',
					onAction: () => {
						setSelectedGroup(undefined)
						setModalOpen(true)
					},
				}}
			/>

			{selectedGroupsToDelete.length > 0 && (
				<ActionBar
					rightActions={[
						{
							label: 'Cancel',
							variant: 'secondary',
							onClick: () => setSelectedGroupsToDelete([]),
						},
						{
							label: `Delete${selectedGroupsToDelete.length > 0 ? ` (${selectedGroupsToDelete.length})` : ''}`,
							variant: 'destructive',
							onClick: () => setIsDeleteDialogOpen(true),
						},
					]}
				/>
			)}

			<Dialog
				isOpen={isDeleteDialogOpen}
				onClose={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleDeleteSelected}
				title='Delete cycle rater groups'
				message={`Are you sure you want to delete ${selectedGroupsToDelete.length} cycle rater group${selectedGroupsToDelete.length === 1 ? '' : 's'}?`}
				type='delete'
				confirmLabel='Delete'
			/>

			<CycleReviewersGroupModal
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
				cycleId={cycle.id}
				organizationId={cycle.organizationId}
				reviewerGroup={selectedGroup}
			/>
		</div>
	)
}

export function CycleReviewersConfigurationTab() {
	const { cycleId } = useParams<{ cycleId: string }>()

	const { data: cycle } = useQuery(
		trpc.admin.cycles.getById.queryOptions({
			id: cycleId as string,
		}),
	)

	return (
		<div className='bg-neutral-0 rounded-lg mt-6'>
			<Text variant={'h6'} weight='semibold' className='text-left'>
				Cycle Settings
			</Text>
			<CycleConfigurationForm cycle={cycle} />
			<CycleReviewerGroups cycle={cycle} />
		</div>
	)
}
